import {
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type AnnouncementListSnapshot,
  fetchAnnouncement,
  fetchAnnouncements,
  type PublicAnnouncement,
} from "@/features/announce/api/announceClient";
import {
  getAnnouncementRetryDelay,
  shouldRetryAnnouncementRequest,
} from "@/features/announce/api/announcementRetryPolicy";
import { getLastGoodQueryData } from "@/features/announce/hooks/announcementLastGood";
import { createAnnouncementMetaQueryOptions } from "@/features/announce/hooks/announcementMetaQuery";
import {
  ANNOUNCEMENTS_PAGE_LIMIT,
  getAnnouncementsPageOffset,
  normalizeAnnouncementsPage,
} from "@/features/announce/lib/announcementPagination";

const DESKTOP_ANNOUNCEMENTS_MODAL_LIMIT = 100;
type ArticleRevision = number | "unknown";

function useArticleRevision(): ArticleRevision {
  const metaQuery = useQuery(createAnnouncementMetaQueryOptions());
  return metaQuery.data?.article_revision ?? "unknown";
}

export function useAnnouncements(
  page: number,
): UseQueryResult<AnnouncementListSnapshot> {
  const normalizedPage = normalizeAnnouncementsPage(page);
  const offset = getAnnouncementsPageOffset(normalizedPage);
  const revision = useArticleRevision();
  const queryClient = useQueryClient();
  const logicalQueryKey = [
    "announce",
    "announcements",
    ANNOUNCEMENTS_PAGE_LIMIT,
    offset,
  ] as const;
  return useQuery({
    queryKey: [...logicalQueryKey, revision],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchAnnouncements(
        ANNOUNCEMENTS_PAGE_LIMIT,
        offset,
        { signal },
        revision === "unknown" ? undefined : revision,
      ),
    initialData: () =>
      getLastGoodQueryData<AnnouncementListSnapshot>(
        queryClient,
        logicalQueryKey,
      ),
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetryAnnouncementRequest,
    retryDelay: getAnnouncementRetryDelay,
  });
}

export function useDesktopModalAnnouncements(): UseQueryResult<AnnouncementListSnapshot> {
  const revision = useArticleRevision();
  const queryClient = useQueryClient();
  const logicalQueryKey = [
    "announce",
    "announcements",
    DESKTOP_ANNOUNCEMENTS_MODAL_LIMIT,
    0,
  ] as const;
  return useQuery({
    queryKey: [...logicalQueryKey, revision],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchAnnouncements(
        DESKTOP_ANNOUNCEMENTS_MODAL_LIMIT,
        0,
        { signal },
        revision === "unknown" ? undefined : revision,
      ),
    initialData: () =>
      getLastGoodQueryData<AnnouncementListSnapshot>(
        queryClient,
        logicalQueryKey,
      ),
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetryAnnouncementRequest,
    retryDelay: getAnnouncementRetryDelay,
  });
}

export function useAnnouncement(
  id: number | null,
): UseQueryResult<PublicAnnouncement> {
  const revision = useArticleRevision();
  const queryClient = useQueryClient();
  const logicalQueryKey = ["announce", "announcement", id] as const;
  return useQuery({
    queryKey: [...logicalQueryKey, revision],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      if (id === null) throw new RangeError("announcement id is required");
      return fetchAnnouncement(
        id,
        { signal },
        revision === "unknown" ? undefined : revision,
      );
    },
    initialData: () =>
      getLastGoodQueryData<PublicAnnouncement>(queryClient, logicalQueryKey),
    initialDataUpdatedAt: 0,
    enabled: id !== null,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetryAnnouncementRequest,
    retryDelay: getAnnouncementRetryDelay,
  });
}
